import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Trash2, MessageCircle, Check, Clock } from "lucide-react";
import { api, resolveImageUri } from "@/lib/api";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFab";
import { toast } from "sonner";

export default function RegistrationsAdmin() {
  const qc = useQueryClient();
  const { data: regs = [] } = useQuery({ queryKey: ["registrations"], queryFn: () => api.listRegistrations() });
  const [filter, setFilter] = useState("all");

  const remove = async (id) => { if (!window.confirm("Delete this registration?")) return; try { await api.deleteRegistration(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["registrations"] }); } catch (err) { toast.error(err.message); } };
  const toggle = async (r) => { try { await api.setRegistrationContacted(r.id, !r.contacted); qc.invalidateQueries({ queryKey: ["registrations"] }); } catch (err) { toast.error(err.message); } };

  const counts = { all: regs.length, products: regs.filter((r) => (r.interested_in || "products") === "products").length, business: regs.filter((r) => r.interested_in === "business").length };
  const filtered = filter === "all" ? regs : regs.filter((r) => (r.interested_in || "products") === filter);

  return (
    <div>
      <h2 className="font-display text-2xl text-ink mb-4">Direct Seller Registrations</h2>
      <div className="flex gap-2 mb-5">
        {[["all", "All"], ["products", "Products"], ["business", "Business"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`h-9 px-4 rounded-full text-sm font-semibold border ${filter === v ? "bg-brand text-white border-brand" : "border-line text-muted2"}`} data-testid={`reg-filter-${v}`}>{l} ({counts[v]})</button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-muted2 text-sm">No registrations.</p>}
        {filtered.map((r) => (
          <div key={r.id} className="rounded-xl border border-line p-4" data-testid={`admin-registration-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-ink">{r.title} {r.name}</p>
                <p className="text-xs text-muted2">{r.mobile} · {r.email}</p>
                <p className="text-xs text-muted2 mt-1">DOB {r.dob} · Nominee {r.nominee_name} ({r.nominee_relation})</p>
                <p className="text-xs text-muted2">{r.address}</p>
                <span className="inline-block text-[11px] font-semibold text-brand bg-brandter rounded-full px-2 py-0.5 mt-1 capitalize">{r.interested_in || "products"}</span>
              </div>
              <button onClick={() => remove(r.id)} className="p-2 text-muted2 hover:text-err shrink-0"><Trash2 size={16} /></button>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {r.pdf_url && <a href={resolveImageUri(r.pdf_url)} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-ink flex items-center gap-1"><Download size={15} /> PDF</a>}
              <a href={`https://wa.me/${(r.mobile || "").replace(/\D/g, "").length >= 10 ? "91" + r.mobile.replace(/\D/g, "").slice(-10) : WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi ${r.name}, thank you for registering with Altos World!`)}`} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#25D366] flex items-center gap-1"><MessageCircle size={15} /> WhatsApp</a>
              <button onClick={() => toggle(r)} className={`text-sm font-semibold flex items-center gap-1 ${r.contacted ? "text-ok" : "text-warn"}`} data-testid={`reg-contacted-${r.id}`}>
                {r.contacted ? <><Check size={15} /> Contacted</> : <><Clock size={15} /> Pending follow-up</>}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
